import { motion } from "framer-motion";
import {
  Zap, Utensils, Bus, GraduationCap, HeartPulse, Home, Building2, Users, KeyRound,
  Wallet, Banknote, PartyPopper, Info, CalendarRange,
} from "lucide-react";
import { MoneyInput, FieldRow } from "@/components/MoneyInput";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ESSENTIALS, SOCIAL_FIELDS, ONEOFF_FIELDS, LOCATIONS, ALL_MONTHS, UTILITY_BILLS } from "@/lib/budget";

const ICONS = { Zap, Utensils, Bus, GraduationCap, HeartPulse };
const fade = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.35 } };

const SectionCard = ({ title, icon: Icon, children, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-[#E8EAEF] bg-white p-5 sm:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
    <div className="mb-3 flex items-center gap-2.5">
      {Icon && (
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EDEBFF] text-[#734FFC]">
          <Icon size={18} />
        </span>
      )}
      <h3 className="text-lg font-semibold text-[#1A1A1A]">{title}</h3>
    </div>
    {children}
  </div>
);

const MonthSelect = ({ value, onChange, testId, months }) => {
  const opts = months || [];
  const safe = Math.min(Math.max(0, value), opts.length - 1);
  return (
    <Select value={String(safe)} onValueChange={(v) => onChange(parseInt(v, 10))}>
      <SelectTrigger data-testid={testId} className="w-24 rounded-xl border-[#E8EAEF]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {opts.map((m, i) => (
          <SelectItem key={`${m}-${i}`} value={String(i)}>{m}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

/* STEP 1 — Income */
export const StepIncome = ({ state, setIncome, setPayment, setTerm, months }) => {
  const inc = state.income;
  const term = state.term;
  const schedules = [
    { id: "3", label: "I receive 3 payments", hint: "Termly" },
    { id: "monthly", label: "I receive monthly", hint: "Spread evenly" },
    { id: "other", label: "Something different", hint: "We'll spread it out" },
  ];
  return (
    <motion.div {...fade} className="space-y-5">
      <SectionCard title="Your study year" icon={CalendarRange} testId="section-term">
        <p className="mb-3 text-sm text-[#747D89]">When does your academic year start, and how many months are you studying? We'll spread your money across these months.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#1A1A1A]">Start month</label>
            <Select value={String(term.startMonth)} onValueChange={(v) => setTerm("startMonth", parseInt(v, 10))}>
              <SelectTrigger data-testid="term-start-month" className="rounded-xl border-[#E8EAEF]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ALL_MONTHS.map((m, i) => (
                  <SelectItem key={m} value={String(i)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#1A1A1A]">Study length</label>
            <Select value={String(term.length)} onValueChange={(v) => setTerm("length", parseInt(v, 10))}>
              <SelectTrigger data-testid="term-length" className="rounded-xl border-[#E8EAEF]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[6, 7, 8, 9, 10, 11, 12].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} months</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="mt-2 text-xs text-[#747D89]">Your year runs {months[0]}–{months[months.length - 1]}.</p>
      </SectionCard>

      <SectionCard title="Student Finance" icon={GraduationCap} testId="section-student-finance">
        <div className="divide-y divide-[#F0F1F4]">
          <FieldRow label="Annual Maintenance Loan" period="per year"><MoneyInput testId="input-maintenanceLoan" value={inc.maintenanceLoan} onChange={(v) => setIncome("maintenanceLoan", v)} /></FieldRow>
          {/* hint shown if no income at all */}
          <FieldRow label="Maintenance Grant" hint="if applicable" period="per year"><MoneyInput testId="input-maintenanceGrant" value={inc.maintenanceGrant} onChange={(v) => setIncome("maintenanceGrant", v)} /></FieldRow>
          <FieldRow label="University Bursary" period="per year"><MoneyInput testId="input-bursary" value={inc.bursary} onChange={(v) => setIncome("bursary", v)} /></FieldRow>
          <FieldRow label="Scholarships" period="per year"><MoneyInput testId="input-scholarships" value={inc.scholarships} onChange={(v) => setIncome("scholarships", v)} /></FieldRow>
        </div>
      </SectionCard>

      <SectionCard title="Other Income" icon={Wallet} testId="section-other-income">
        <div className="divide-y divide-[#F0F1F4]">
          <FieldRow label="Parents / Family Support" period="per month"><MoneyInput testId="input-familySupport" value={inc.familySupport} onChange={(v) => setIncome("familySupport", v)} /></FieldRow>
          <FieldRow label="Part-time Job" hint="average take-home" period="per month"><MoneyInput testId="input-partTime" value={inc.partTime} onChange={(v) => setIncome("partTime", v)} /></FieldRow>
          <FieldRow label="Savings you'll use" period="total for year"><MoneyInput testId="input-savings" value={inc.savings} onChange={(v) => setIncome("savings", v)} /></FieldRow>
          <FieldRow label="Other income" period="per year"><MoneyInput testId="input-other" value={inc.other} onChange={(v) => setIncome("other", v)} /></FieldRow>
        </div>
      </SectionCard>

      <SectionCard title="Payment Schedule" icon={Banknote} testId="section-schedule">
        <p className="mb-3 text-sm text-[#747D89]">Student Finance usually arrives three times each year.</p>
        <div className="grid gap-2.5 sm:grid-cols-3">
          {schedules.map((s) => {
            const active = inc.schedule === s.id;
            return (
              <button
                key={s.id}
                data-testid={`schedule-${s.id}`}
                onClick={() => setIncome("schedule", s.id)}
                className={`rounded-xl border p-3 text-left transition-colors duration-150 ${active ? "border-[#734FFC] bg-[#EDEBFF]" : "border-[#E8EAEF] bg-white hover:border-[#C7BAFC]"}`}
              >
                <span className="block text-sm font-semibold text-[#1A1A1A]">{s.label}</span>
                <span className="block text-xs text-[#747D89]">{s.hint}</span>
              </button>
            );
          })}
        </div>
        {inc.schedule === "3" && (
          <div className="mt-4 space-y-2 rounded-xl bg-[#FBFAF7] p-4">
            {inc.payments.map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-[#1A1A1A]">Payment {i + 1}</span>
                <div className="flex items-center gap-2">
                  <MoneyInput className="w-28" testId={`payment-amount-${i}`} value={p.amount} onChange={(v) => setPayment(i, "amount", v)} />
                  <MonthSelect testId={`payment-month-${i}`} months={months} value={p.month} onChange={(v) => setPayment(i, "month", v)} />
                </div>
              </div>
            ))}
            <p className="pt-1 text-xs text-[#747D89]">Leave blank to split your finance evenly across the three terms.</p>
          </div>
        )}
      </SectionCard>
    </motion.div>
  );
};

/* STEP 2 — Living */
export const StepLiving = ({ state, setLiving }) => {
  const { living } = state;
  const icons = { home: Home, halls: Building2, shared: Users, private: KeyRound };
  return (
    <motion.div {...fade} className="space-y-5">
      <SectionCard title="Where you live" testId="section-living">
        <div className="grid gap-2.5 sm:grid-cols-2">
          {LOCATIONS.map((loc) => {
            const Icon = icons[loc.id];
            const active = living.location === loc.id;
            return (
              <button
                key={loc.id}
                data-testid={`location-${loc.id}`}
                onClick={() => setLiving("location", loc.id)}
                className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors duration-150 ${active ? "border-[#734FFC] bg-[#EDEBFF]" : "border-[#E8EAEF] bg-white hover:border-[#C7BAFC]"}`}
              >
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${active ? "bg-[#734FFC] text-white" : "bg-[#EDEBFF] text-[#734FFC]"}`}>
                  <Icon size={18} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-[#1A1A1A]">{loc.label}</span>
                  <span className="block text-xs text-[#747D89]">{loc.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard title="Rent" testId="section-rent">
        <FieldRow label="Rent" period="per month" required><MoneyInput testId="input-rent" value={living.rent} onChange={(v) => setLiving("rent", v)} required /></FieldRow>
        <div className="mt-3 flex items-center justify-between rounded-xl bg-[#FBFAF7] p-4">
          <div>
            <p className="text-sm font-medium text-[#1A1A1A]">Includes bills?</p>
            <p className="text-xs text-[#747D89]">Toggle on if gas, electric, water &amp; internet are part of your rent — we'll mark them as covered in your Bills.</p>
          </div>
          <Switch data-testid="toggle-includes-bills" checked={living.includesBills} onCheckedChange={(v) => setLiving("includesBills", v)} />
        </div>
      </SectionCard>
    </motion.div>
  );
};

/* STEP 3 — Monthly Essentials */
export const StepEssentials = ({ state, setSection }) => {
  const includesBills = !!state.living.includesBills;
  return (
    <motion.div {...fade} className="grid gap-5 md:grid-cols-2">
      {Object.entries(ESSENTIALS).map(([key, cfg]) => {
        const Icon = ICONS[cfg.icon];
        const isBills = key === "bills";
        return (
          <SectionCard key={key} title={cfg.label} icon={Icon} testId={`section-${key}`}>
            {isBills && includesBills && (
              <div data-testid="bills-included-note" className="mb-3 rounded-lg bg-[#EDEBFF] px-3 py-2 text-xs font-medium text-[#734FFC]">
                Your rent includes bills, so gas, electric, water &amp; internet are already covered.
              </div>
            )}
            <div className="divide-y divide-[#F0F1F4]">
              {cfg.fields.map(([fk, fl]) => {
                const covered = isBills && includesBills && UTILITY_BILLS.includes(fk);
                return (
                  <FieldRow key={fk} label={fl} period={covered ? "in rent" : "per month"}>
                    {covered ? (
                      <div data-testid={`bill-covered-${fk}`} className="flex h-[42px] items-center justify-center rounded-xl border border-dashed border-[#C7BAFC] bg-[#EDEBFF] text-xs font-medium text-[#734FFC]">
                        Included in rent
                      </div>
                    ) : (
                      <MoneyInput testId={`input-${key}-${fk}`} value={state[key][fk]} onChange={(v) => setSection(key, fk, v)} />
                    )}
                  </FieldRow>
                );
              })}
            </div>
          </SectionCard>
        );
      })}
    </motion.div>
  );
};

/* STEP 4 — Social Life */
export const StepSocial = ({ state, setSection }) => (
  <motion.div {...fade}>
    <SectionCard title="Social Life" icon={PartyPopper} testId="section-social">
      <p className="mb-3 text-sm text-[#747D89]">Roughly how much do you spend each month on the fun stuff?</p>
      <div className="grid gap-x-8 sm:grid-cols-2">
        {SOCIAL_FIELDS.map(([fk, fl]) => (
          <FieldRow key={fk} label={fl} period="per month">
            <MoneyInput testId={`input-social-${fk}`} value={state.social[fk]} onChange={(v) => setSection("social", fk, v)} />
          </FieldRow>
        ))}
      </div>
    </SectionCard>
  </motion.div>
);

/* STEP 5 — One-off Costs */
export const StepOneOffs = ({ state, setOneOff, months }) => (
  <motion.div {...fade}>
    <SectionCard title="One-off Costs" icon={Info} testId="section-oneoffs">
      <div className="mb-4 flex items-start gap-2 rounded-xl bg-[#EDEBFF] p-3 text-sm text-[#734FFC]">
        <Info size={16} className="mt-0.5 shrink-0" />
        <p>Students often forget these. We'll spread each cost across the year so it doesn't catch you out.</p>
      </div>
      <div className="space-y-2.5">
        {ONEOFF_FIELDS.map(([fk, fl]) => (
          <div key={fk} className="flex items-center justify-between gap-3 border-b border-[#F0F1F4] py-1.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[#1A1A1A]">{fl}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8E99A8]">one-off &middot; spread across the year</p>
            </div>
            <div className="flex items-center gap-2">
              <MoneyInput className="w-28" testId={`oneoff-amount-${fk}`} value={state.oneOffs[fk].amount} onChange={(v) => setOneOff(fk, "amount", v)} />
              <MonthSelect testId={`oneoff-month-${fk}`} months={months} value={state.oneOffs[fk].month} onChange={(v) => setOneOff(fk, "month", v)} />
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  </motion.div>
);
